import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as htmlToImage from 'html-to-image';
import { Sparkles, Clock, Sprout, CheckCircle, CalendarDays, Shirt, Download, ChevronLeft, ChevronRight, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MonthlyRecap({ pet }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API}/stats/monthly-recap?year=${cursor.year}&month=${cursor.month}`,
          { withCredentials: true }
        );
        setData(data);
      } catch { /* noop */ }
      finally { setLoading(false); }
    })();
  }, [cursor]);

  const prev = () => setCursor((c) => c.month === 1 ? { year: c.year - 1, month: 12 } : { year: c.year, month: c.month - 1 });
  const next = () => setCursor((c) => c.month === 12 ? { year: c.year + 1, month: 1 } : { year: c.year, month: c.month + 1 });

  const exportImage = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await htmlToImage.toPng(cardRef.current, {
        backgroundColor: '#FFE8F0',
        pixelRatio: 2,
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `min-manad-${cursor.year}-${String(cursor.month).padStart(2, '0')}.png`;
      a.click();
      toast.success('Sparad som bild ✨');
    } catch { toast.error('Kunde inte exportera'); }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar sammanfattning…</div>;
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="monthly-recap">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-2xl font-bold font-['Fredoka'] flex items-center gap-2">
          <Trophy className="w-6 h-6" /> Månadssammanfattning
        </h2>
        <div className="flex gap-2">
          <button onClick={prev} className="neo-btn p-2 bg-white"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={next} className="neo-btn p-2 bg-white"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={exportImage} className="neo-btn px-3 py-2 bg-[#C8B6FF] text-xs font-bold flex items-center gap-1" data-testid="recap-export">
            <Download className="w-3 h-3" /> Exportera
          </button>
        </div>
      </div>

      <div
        ref={cardRef}
        className="relative rounded-3xl border-[3px] border-black p-6 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #FFE8F0 0%, #F3E8FF 50%, #E8F0FF 100%)',
          boxShadow: '5px 5px 0px 0px rgba(0,0,0,1)',
        }}
      >
        {/* Pet + headline */}
        <div className="text-center mb-5">
          {pet?.image && (
            <div className="inline-block w-20 h-20 rounded-full border-[3px] border-black overflow-hidden shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] mb-2">
              <img src={pet.image} alt={pet.name} className="w-full h-full object-cover" />
            </div>
          )}
          <h3 className="text-3xl font-bold font-['Fredoka'] mt-1">{data.month_label}</h3>
          <p className="text-sm italic opacity-75 mt-1">En månad att vara stolt över ✨</p>
        </div>

        {/* Big stat */}
        <div className="text-center mb-5 py-4 rounded-2xl border-2 border-black bg-white/60">
          <p className="text-[10px] font-bold uppercase tracking-widest opacity-60">Månadens poäng</p>
          <p className="text-5xl font-bold font-['Fredoka'] text-[#C8B6FF]" style={{ WebkitTextStroke: '2px black' }}>
            {data.total_score}
          </p>
          <p className="text-xs opacity-70 mt-1">{data.active_days} aktiva dagar · {data.current_streak} streak 🔥</p>
        </div>

        {/* Grid of stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <StatTile
            icon={<Clock className="w-4 h-4" />}
            label="Focus"
            value={`${data.focus.sessions}`}
            sub={`${data.focus.hours}h totalt`}
            color="#A2D2FF"
          />
          <StatTile
            icon={<Sprout className="w-4 h-4" />}
            label="Städuppgifter"
            value={`${data.cleaning.count}`}
            sub={`+${data.cleaning.points} p`}
            color="#B9FBC0"
          />
          <StatTile
            icon={<CheckCircle className="w-4 h-4" />}
            label="Tasks klara"
            value={`${data.tasks_done}`}
            sub=""
            color="#FFD6E8"
          />
          <StatTile
            icon={<CalendarDays className="w-4 h-4" />}
            label="Händelser"
            value={`${data.events_done}`}
            sub="genomförda"
            color="#C8B6FF"
          />
          <StatTile
            icon={<Shirt className="w-4 h-4" />}
            label="Outfits"
            value={`${data.outfits_generated}`}
            sub="genererade"
            color="#FDF0D5"
          />
          <StatTile
            icon={<Trophy className="w-4 h-4" />}
            label="Bucket list"
            value={`${data.bucket_done}`}
            sub="avbockade"
            color="#FFF4C0"
          />
        </div>

        {/* Confetti sparkles */}
        <span className="absolute top-4 right-6 text-2xl opacity-30">✨</span>
        <span className="absolute top-12 left-8 text-lg opacity-20">💖</span>
        <span className="absolute bottom-8 right-12 text-xl opacity-25">🎉</span>
        <span className="absolute bottom-20 left-6 text-base opacity-20">💫</span>

        {/* Watermark */}
        <p className="text-center text-[10px] opacity-40 mt-5 font-mono">Focus Critter · focuscritter.app</p>
      </div>

      <p className="text-[11px] opacity-60 text-center italic">💡 Klicka "Exportera" för att spara som bild & dela</p>
    </div>
  );
}

function StatTile({ icon, label, value, sub, color }) {
  return (
    <div className="rounded-2xl border-2 border-black p-3 text-center" style={{ backgroundColor: color }}>
      <div className="flex items-center justify-center gap-1 mb-1">
        {icon}
        <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</p>
      </div>
      <p className="text-3xl font-bold font-['Fredoka']">{value}</p>
      {sub && <p className="text-[10px] opacity-70">{sub}</p>}
    </div>
  );
}
